import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage76() {
  const contentRef = useRef(null);
  const titleText = "Anatomy of A Bitcoin Block and its Propgation";
  const links = {
    prev: "/bitcoin-tech-page-75",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-77",
  };

  const questions = [
    {
      text: "1. What is the primary role of the block header in Bitcoin?",
      options: [
        {
          value: "A",
          label: "A. To store all transaction data in the block",
        },
        {
          value: "B",
          label:
            "B. To contain metadata required for mining and block validation",
        },
        {
          value: "C",
          label: "C. To list wallet balances for all users",
        },
        {
          value: "D",
          label: "D. To record transaction fees only",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Why is the Merkle root included in the block header?",
      options: [
        {
          value: "A",
          label: "A. To encrypt transactions inside the block",
        },
        {
          value: "B",
          label:
            "B. To summarize all transactions and allow efficient verification",
        },
        {
          value: "C",
          label: "C. To increase the block size limit",
        },
        {
          value: "D",
          label: "D. To store miner rewards",
        },
      ],
      correct: "B",
    },
    {
      text: "3. What is the main advantage of compact block relay (BIP 152) over full block relay?",
      options: [
        {
          value: "A",
          label: "A. It removes the need for transaction validation",
        },
        {
          value: "B",
          label:
            "B. It reduces bandwidth usage by sending short transaction identifiers",
        },
        {
          value: "C",
          label: "C. It allows blocks to be mined faster",
        },
        {
          value: "D",
          label: "D. It guarantees blocks will never conflict",
        },
      ],
      correct: "B",
    },
    {
      text: "4. How does Bitcoin resolve a block race when two valid blocks are found simultaneously?",
      options: [
        {
          value: "A",
          label: "A. Nodes randomly choose which block to keep",
        },
        {
          value: "B",
          label: "B. The block with higher transaction fees always wins",
        },
        {
          value: "C",
          label:
            "C. The chain with the greatest cumulative proof of work is selected",
        },
        {
          value: "D",
          label: "D. The earliest timestamped block becomes final",
        },
      ],
      correct: "C",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={76}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content">
        Welcome to <b>Day 76</b>! In this lesson, we explore the anatomy of a
        Bitcoin block and how blocks propagate across the network. You will
        learn how blocks are structured, why each component matters for security
        and validation, and how miners and nodes efficiently relay new blocks to
        maintain global consensus. We will also examine compact blocks,
        bandwidth optimization, and what happens when two miners discover a
        block at the same time, highlighting how Bitcoin reliably resolves
        temporary conflicts.
        <br />
        <br />
        <b>What is a Bitcoin Block?</b>
        <br />
        <br />
        A Bitcoin block is a container data structure that records a collection
        of Bitcoin transactions, grouped together and confirmed by the network.
        Every block is permanently recorded in the Bitcoin blockchain — a
        continuously growing ledger that is distributed across all Bitcoin
        nodes.
        <br />
        Each block links to the previous one using a cryptographic hash, forming
        a chronological chain. This linking mechanism provides security and
        immutability, as altering a block would require changing all subsequent
        blocks, which is computationally unfeasible.
        <br />
        Blocks are produced roughly every 10 minutes through a process called
        mining, in which miners compete to solve a computationally intensive
        puzzle. The first miner to solve the puzzle gets to create the next
        block and collect the associated rewards.
        <br />
        <br />
        <b>Components of a Bitcoin Block and Their Significance</b>
        <br />
        <br />
        A Bitcoin block is composed of two main parts: the block header and the
        block body.
        <br />
        <br />
        <b>1. Block Header</b> (80 bytes)
        <br />
        <br />
        The block header contains metadata about the block and plays a crucial
        role in the mining process. It includes the following fields:
        <ul>
          <li>
            Version: Indicates the version of the block and the set of consensus
            rules that apply.
          </li>
          <li>
            Previous Block Hash: The 32-byte SHA-256 hash of the previous
            block’s header.
          </li>
          <li>
            Merkle Root: A 32-byte hash derived from all the transactions in the
            block using a Merkle tree.
          </li>
          <li>
            Timestamp: The approximate time when the block was mined,
            represented in Unix epoch format.
          </li>
          <li>
            Bits: A representation of the current mining difficulty target.
          </li>
          <li>
            Nonce: A 32-bit number that miners vary to try and produce a block
            hash below the required difficulty target.
          </li>
        </ul>
        <b>2. Transaction Counter</b>
        <br />
        <br />
        This is a variable-length field that indicates how many transactions are
        included in the block.
        <br />
        <br />
        <b>3. Transactions</b>
        <br />
        <br />
        The block contains a list of transactions, starting with a special one
        called the coinbase transaction.
        <br />
        <br />
        <b>Coinbase Transaction</b>
        <br />
        <br />
        The coinbase transaction is the first transaction in every block and is
        created by the miner of that block.
        <br /> On the Bitcoin network, every transaction that takes place is
        grouped together to form what is known as a block. Once a block is
        assembled, it is immediately added to the blockchain. These blocks are
        designed to be permanent and resistant to tampering, ensuring the
        integrity of all recorded transactions. A valid block must include at
        least one transaction, and the very first transaction in every block is
        known as the coinbase transaction.
        <br />
        The process of creating new blocks is carried out by miners. When a
        miner successfully produces a block, they receive a reward from the
        Bitcoin system in return for their computational effort. This reward is
        made up of two parts: the fixed block subsidy, which decreases over time
        based on how many blocks have been mined since the original genesis
        block, and the total transaction fees collected from all the
        transactions included in that block.
        <br />
        Together, the block subsidy and transaction fees form the complete
        reward that miners earn each time they add a new block to the
        blockchain.
        <br />
        This reward consists of:
        <ul>
          <li>The block subsidy (new bitcoins introduced into circulation)</li>
          <li>
            The sum of transaction fees from all transactions in the block
          </li>
        </ul>
        A notable rule is that the outputs of the coinbase transaction cannot be
        spent until 100 more blocks have been added to the blockchain.
        <br />
        <br />
        <b>Merkle Tree and Its Role in a Block</b>
        <br />
        A Merkle tree is a binary hash tree that organizes and summarizes all
        the transactions in a block.
        <br />
        <br />
        The key properties of a Merkle tree are:
        <ul>
          <li>
            It enables efficient and secure verification of transactions without
            needing to download the entire block.
          </li>
          <li> It supports Simplified Payment Verification (SPV) clients.</li>
          <li>It contributes to block header size optimization.</li>
        </ul>
        If any transaction changes, the entire Merkle root changes, which would
        invalidate the block header’s hash and thus the entire block.
        <br />
        <br />
        <b>How Full Blocks and Compact Blocks Are Relayed Among Peers</b>
        <br />
        <br />
        <b>Full Block Relay</b>
        <br />
        <br />
        In the traditional method of block propagation, often referred to as
        full block relay, a newly mined block is transmitted from one node to
        another in its complete form. This means that all transaction data
        included in the block is sent in full, regardless of whether the
        receiving peer already has any of the transactions in its mempool.
        <br />
        When a node learns about a new block, it downloads the entire block
        directly from one of its peers. This full block contains the block
        header and every transaction in raw format. Since miners typically
        construct blocks using transactions already circulating in the network,
        there is a high likelihood that the receiving node has already seen many
        of these transactions. However, in full relay, no assumptions are made
        about this overlap, so all transactions are resent.
        <br />
        This method guarantees that every node gets an exact and complete copy
        of the block as quickly as possible. It is simple and reliable but can
        be inefficient in terms of bandwidth usage. For large blocks containing
        thousands of transactions, transmitting the full block data to multiple
        peers can consume a significant amount of network resources.
        <br />
        Full relay also increases latency, especially over connections with
        limited bandwidth. Since each transaction can be several hundred bytes
        long, and a block can be up to four megabytes in size, transmitting the
        entire block introduces noticeable delay. The receiving peer must also
        wait for the full data before it can begin verification, further slowing
        down the propagation process.
        <br />
        Despite its drawbacks, full block relay remains a critical fallback
        mechanism. It is used when more efficient methods like compact block
        relay cannot be used effectively. For instance, if a peer has a
        significantly different mempool or is missing many transactions due to
        recent disconnections, compact relay may fail to reconstruct the block.
        In such cases, the peer will request the entire block through full
        relay.
        <br />
        The protocol used for full block transmission includes an inventory
        message to announce the block's availability, followed by a request from
        the receiving peer. Once the block is requested, it is sent using a
        block message that contains all relevant information. The receiving node
        then performs full validation, checking proof of work, structure, and
        individual transactions.
        <br />
        Full relay is also essential for new nodes syncing the blockchain for
        the first time. These nodes do not have any previous transactions in
        their mempool, so they must receive the entire block data in order to
        validate and store it.
        <br />
        While less efficient than newer methods, full relay continues to be an
        important part of block propagation. Its robustness ensures that blocks
        can still be delivered under poor network conditions or when optimized
        relay strategies are unavailable.
        <br />
        <br />
        This traditional method involves sending the entire block i.e header and
        all transactions to peer nodes. <br />
        <br />
        1. The Reciever receives the complete block.
        <br />
        2. Validates the block header.
        <br />
        3. Validates each transaction.
        <br />
        4. Adds the block to its local chain if valid.
        <br />
        <br />
        <b>Compact Block Relay (BIP 152)</b>
        <br />
        <br />
        Introduced to improve bandwidth efficiency and propagation speed.
        <br />
        <br />
        Compact block relay, described in BIP152, is a technique that helps
        Bitcoin nodes share newly mined blocks more efficiently. It reduces both
        the size of data being transmitted and the time it takes for blocks to
        reach peers. This approach works best when nodes already have most of
        the transactions in a block stored in their mempools.
        <br />
        Normally, when a miner creates a block, it includes transactions that
        were previously broadcast across the network. Since these transactions
        have already reached many nodes, including them again during block relay
        results in duplicated data transmission.
        <br />
        To solve this, compact blocks replace each known transaction with a
        short 6-byte identifier derived from the transaction ID or the witness
        transaction ID, depending on the protocol version. These identifiers are
        not cryptographic but are fast to compute and sufficient for matching
        transactions. This process shrinks the block size dramatically, often
        reducing it from several megabytes to just a few kilobytes. As a result,
        less bandwidth is consumed, and the smaller data size also leads to
        faster propagation.
        <br />
        When a node creates a compact block, it only includes full transaction
        data for those it assumes the receiving peer does not yet have. Upon
        receiving the compact block, the peer matches the short transaction
        identifiers with the transactions stored in its mempool. If it finds a
        match, it inserts the full transaction into the block. If it cannot find
        a match, it sends a request to the sender to receive the missing
        transaction.
        <br />
        There are two relay modes supported by compact blocks.
        <br />
        In low bandwidth mode, a node first announces the header of a new block
        to its peers. Only if a peer needs the block does it request the compact
        version. This helps avoid sending unnecessary data to peers that may
        have already received the block from someone else.
        <br />
        In high bandwidth mode, used with a limited number of peers, a node
        immediately sends out the compact block as soon as it receives and
        minimally verifies it. This approach prioritizes speed and is useful for
        fast relay across the network. Even though it sometimes results in
        sending the same data more than once, the small size of compact blocks
        makes this mode still more efficient than older relay methods.
        <br />
        BIP152 defines two protocol versions. Version 1 uses hashes based on the
        regular transaction IDs, while Version 2 uses hashes derived from the
        witness transaction IDs. The second version is compatible with
        Segregated Witness, which changes how signatures are stored and
        validated.
        <br />
        To make the most of compact block relay, it is important that nodes
        maintain similar transaction policies and mempool contents. If nodes
        differ significantly in what transactions they store, more data will
        need to be sent, which reduces the efficiency of the protocol.
        <br />
        Overall, compact block relay helps speed up block propagation and reduce
        data usage by taking advantage of shared knowledge across the network.
        <br />
        <br />
        Here's how the process works:
        <br />
        1. The sending node constructs a compact block.
        <br />
        2. The receiving node tries to reconstruct the full block.
        <br />
        3. If any transactions are missing, the receiving node requests them.
        <br />
        4. The sender replies with the missing transactions.
        <br />
        5. Once the full block is reconstructed, it is validated and relayed as
        normal.
        <br />
        <br />
        <b>Block Race!</b>
        <br />
        <br />
        What if two miners find a valid block at the same time and propogate it
        to the network? <br />
        Well, this is what we call a 'Block Race'.
        <br />
        <br />
        A block race in Bitcoin occurs when two miners, often located in
        different parts of the world, discover valid blocks at nearly the same
        moment and begin broadcasting them to the rest of the network. Since
        Bitcoin is a decentralized system where nodes are spread globally and
        receive data with varying delays, not all nodes see the same block
        first. As a result, some nodes will receive one version of the next
        block before the other, and temporarily, the network will be split
        between two competing views of what the latest block is.
        <br />
        This situation leads to what’s known as a temporary chain split. Each
        version of the new block extends the same parent block and is therefore
        considered equally valid at first. Some miners will start mining on top
        of the first block they receive, while others will mine on the competing
        block. For a short period, there are effectively two versions of the
        blockchain being built simultaneously.
        <br />
        The resolution of this conflict comes through the principle of
        accumulated proof of work. When another miner finds a new block that
        builds upon one of the competing chains, that chain becomes longer and
        is considered to have more proof of work. According to Bitcoin's
        consensus rules, nodes always switch to the valid chain with the
        greatest cumulative work. This means the shorter branch will be
        abandoned, and the block or blocks in that chain will be considered
        orphaned. Although the orphaned blocks were valid, they are no longer
        part of the main chain and their transactions (except the coinbase
        transaction) are typically re-added to the mempool for inclusion in
        future blocks.
        <br />
        The use of proof of work in determining the winning chain ensures that
        no single party can unilaterally declare their block as final. Instead,
        the entire network reaches agreement based on the amount of
        computational effort invested. This method of resolving competing chains
        is crucial for maintaining the integrity and consistency of the Bitcoin
        ledger across a distributed network. It ensures that all honest nodes
        eventually converge on a single chain, even if brief disagreements occur
        due to network latency or simultaneous block discovery.
        <br /> Understanding Bitcoin blocks and their propagation is key to
        appreciating the elegance and robustness of Bitcoin's design.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        This lesson explored the structure of a Bitcoin block and how blocks are
        propagated across the network. You learned how blocks are formed from
        transactions, how the block header secures each block through
        cryptographic linking and proof of work, and why components like the
        Merkle root are critical for verification and efficiency. The lesson
        also explained how blocks travel through the network using full block
        relay and compact block relay, highlighting the trade-offs between
        reliability and bandwidth efficiency. Finally, it covered block races
        and how Bitcoin resolves temporary chain splits using accumulated proof
        of work, ensuring that the network consistently converges on a single,
        valid blockchain.
      </p>

      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage76;
